import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import {
  BadgeCheck,
  ClipboardList,
  FileText,
  GraduationCap,
  ScrollText,
  TrendingUp,
  TriangleAlert,
} from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import OnboardingLayout from "@/components/layout/OnboardingLayout";
import { useAuthStore } from "@/store/authStore";
import {
  useUniversities,
  useProgrammes,
  useIndustries,
  useSkills,
  UniversityOption,
  ProgrammeOption,
} from "@/hooks/useReferenceData";
import apiClient, { setAuthToken } from "@/lib/api-client";
import { Field } from "@/components/forms";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiCombobox } from "@/components/ui/multi-combobox";
import {
  DocumentUploadRow,
  OnboardingComplete,
  StepHeader,
} from "@/components/onboarding/OnboardingParts";
import { cn } from "@/lib/utils";
import { DatePicker } from "@/components/ui/date-picker";

export const Route = createFileRoute("/onboarding/student")({
  component: StudentOnboarding,
});

const steps = ["Academic Background", "Credentialing", "Professional Path"];

function StudentOnboarding() {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    phone: "",
    dob: "",
    gender: "",
    university: "",
    universityId: null as any,
    course: "",
    year: "",
    studentId: "",
    careerGoals: "",
    selectedSkills: [] as string[],
    preferredIndustry: "",
    preferredDays: [] as string[],
  });
  const [files, setFiles] = useState<Record<string, File | null>>({
    deanLetter: null,
    universityId: null,
    cv: null,
  });
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user, setUser } = useAuthStore();
  const navigate = useNavigate();

  // Student onboarding writes a student profile, which the API only allows for
  // student and not-yet-assigned accounts. Saying so up front beats a 403 at the
  // last step (for example when testing with the seeded admin account).
  const roleCanOnboard = !user?.role || user.role === "student" || user.role === "unassigned";

  // Errors are shown once the person has tried to continue, and then follow
  // their edits so a fixed field clears straight away.
  const [showErrors, setShowErrors] = useState(false);
  const validateStep = (step: number): Record<string, string> => {
    const e: Record<string, string> = {};
    if (step === 0) {
      if (!formData.dob) {
        e.dob = "Enter your date of birth.";
      } else if (new Date(formData.dob) > new Date()) {
        e.dob = "Date of birth can't be in the future.";
      }
      if (!formData.studentId.trim()) e.studentId = "Enter your official student ID.";
      if (!formData.university) e.university = "Select your university or college.";
      if (!formData.course.trim()) e.course = "Select or type your programme.";
      if (!formData.year) e.year = "Choose your current year of study.";
    }
    return e;
  };
  const errors = showErrors ? validateStep(activeStep) : {};

  const fieldLabels: Record<string, string> = {
    dob: "date of birth",
    studentId: "student ID",
    university: "university",
    course: "programme",
    year: "year of study",
  };

  // Turns an API failure into a sentence that says what to fix. The global
  // handler is skipped for these calls so the person sees one specific toast.
  const describeApiError = (err: any): string => {
    const data = err?.response?.data;
    if (!err?.response) return "We couldn't reach the server. Check your connection and try again.";
    if (err.response.status === 401) return "Your session has expired. Please sign in again.";
    if (
      typeof data?.error === "string" &&
      data.error &&
      data.error !== "ROLE_NOT_ALLOWED" &&
      data.message === "Invalid request"
    ) {
      return `Some details were not accepted: ${data.error}`;
    }
    return data?.message || "Something went wrong. Please try again.";
  };

  const calculateProgress = () => {
    const totalFields = 10;
    let filledFields = 0;
    if (formData.dob) filledFields++;
    if (formData.studentId) filledFields++;
    if (formData.university) filledFields++;
    if (formData.course) filledFields++;
    if (formData.year) filledFields++;
    if (files.deanLetter) filledFields++;
    if (files.universityId) filledFields++;
    if (files.cv) filledFields++;
    if (formData.selectedSkills.length > 0) filledFields++;
    if (formData.careerGoals) filledFields++;
    return (filledFields / totalFields) * 100;
  };

  const [uniSearch, setUniSearch] = useState("");
  const [progSearch, setProgSearch] = useState("");
  const { data: universities, isLoading: uniLoading } = useUniversities(
    uniSearch,
    formData.universityId,
  );
  const { data: programmes, isLoading: progLoading } = useProgrammes(
    formData.universityId ?? undefined,
    progSearch,
  );
  const { data: industriesData } = useIndustries();
  const { data: skillsData } = useSkills();

  const handleNext = async () => {
    const stepErrors = validateStep(activeStep);
    if (Object.keys(stepErrors).length > 0) {
      setShowErrors(true);
      toast.error("Please complete the highlighted fields", {
        description: `Missing or invalid: ${Object.keys(stepErrors)
          .map((k) => fieldLabels[k] || k)
          .join(", ")}.`,
      });
      return;
    }
    setShowErrors(false);

    if (activeStep === steps.length - 1) {
      if (!roleCanOnboard) {
        toast.error("This account can't complete student onboarding", {
          description: `You are signed in as ${user?.role === "admin" ? "an admin" : `a ${user?.role}`} account. Sign out and use a student account.`,
        });
        return;
      }
      let stage = "save your profile";
      setLoading(true);
      try {
        const parts = (user?.name || "").trim().split(/\s+/);
        const firstName = parts[0] || "";
        const lastName = parts.slice(1).join(" ") || "";

        // 1. Update Profile
        await apiClient.patch(
          "/students/me",
          {
            first_name: firstName,
            last_name: lastName,
            student_id: formData.studentId.trim(),
            university: formData.university,
            major: formData.course,
            year_of_study: parseInt(formData.year),
            bio: formData.careerGoals,
            skills: formData.selectedSkills.join(","),
            phone: formData.phone,
            address: formData.preferredIndustry, // Using industry as a proxy for preferred placement sector
          },
          { skipGlobalErrorHandler: true } as any,
        );

        // 2. Upload Documents
        const uploadDoc = async (file: File, type: string) => {
          stage = `upload your ${file.name}`;
          const form = new FormData();
          form.append("file", file);
          form.append("type", type);
          await apiClient.post("/students/me/documents", form, {
            headers: { "Content-Type": "multipart/form-data" },
            skipGlobalErrorHandler: true,
          } as any);
        };

        if (files.deanLetter) await uploadDoc(files.deanLetter, "dean_letter");
        if (files.universityId) await uploadDoc(files.universityId, "student_id");
        if (files.cv) await uploadDoc(files.cv, "resume");

        // 3. Refresh session to get new token with 'student' role
        try {
          const refreshRes = await apiClient.post("/auth/refresh");
          const newToken = refreshRes.data.data.token;
          if (newToken) setAuthToken(newToken);

          const meRes = await apiClient.get("/auth/me");
          setUser(meRes.data.data);
        } catch (refreshErr) {
          console.error("Failed to refresh session:", refreshErr);
          if (user) {
            setUser({
              ...user,
              role: "student",
              name: `${firstName} ${lastName}`.trim(),
              status: "active",
            });
          }
        }

        setCompleted(true);
      } catch (err: any) {
        console.error("Onboarding failed:", err);
        toast.error(`We couldn't ${stage}`, { description: describeApiError(err) });
      } finally {
        setLoading(false);
      }
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const stepIcons = [<GraduationCap />, <ScrollText />, <TrendingUp />];

  // University options come from the server-side search. The current selection is
  // kept in the list even when the latest search no longer returns it.
  const uniKey = (u: UniversityOption) => String(u.id || (u as UniversityOption & { ID?: number }).ID);
  const universityOptions: ComboboxOption[] = (universities || []).map((u: UniversityOption) => ({
    value: uniKey(u),
    label: u.name,
    description: `${u.abbreviation} • ${u.location} • ${u.type}`,
  }));
  const selectedUniKey =
    formData.universityId != null && formData.universityId !== ""
      ? String(formData.universityId)
      : formData.university
        ? "__current__"
        : null;
  if (
    formData.university &&
    selectedUniKey &&
    !universityOptions.some((o) => o.value === selectedUniKey)
  ) {
    universityOptions.unshift({ value: selectedUniKey, label: formData.university });
  }

  // Programmes are matched by name, as before; duplicates by name collapse to one entry.
  const programmeOptions: ComboboxOption[] = [];
  (programmes || []).forEach((p: ProgrammeOption) => {
    if (programmeOptions.some((o) => o.value === p.name)) return;
    programmeOptions.push({
      value: p.name,
      label: p.name,
      description: `${p.award_level}${p.duration ? ` • ${p.duration}` : ""}${p.study_mode ? ` • ${p.study_mode}` : ""}`,
    });
  });
  if (formData.course && !programmeOptions.some((o) => o.value === formData.course)) {
    programmeOptions.unshift({ value: formData.course, label: formData.course });
  }

  const skillOptions = (skillsData || []).map((s: string | { name: string }) => {
    const name = typeof s === "string" ? s : s.name;
    return { value: name, label: name };
  });

  if (completed) {
    return (
      <OnboardingLayout
        title="Onboarding Fulfilled"
        subtitle="Your professional student profile is now calibrated and live in the ecosystem."
      >
        <OnboardingComplete
          title="Welcome Aboard!"
          body="Your academic profile has been established. You can now start searching for industrial placements that align with your career trajectory."
          status="Ready for placement matching"
          to="/student/dashboard"
          cta="Launch Student Dashboard"
        />
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      step={activeStep}
      totalSteps={steps.length}
      fieldProgress={calculateProgress()}
      title="Scale your career"
      subtitle="Complete your profile to unlock premium industrial placements."
    >
      <div className="w-full">
        <StepHeader
          icon={stepIcons[activeStep]}
          title={steps[activeStep]}
          description={
            activeStep === 0
              ? "Provide your primary academic background."
              : activeStep === 1
                ? "Upload your official student credentials."
                : "Define your skills and career objectives."
          }
        />

        <div className="min-h-[340px] w-full text-left">
          {!roleCanOnboard && (
            <Alert variant="warning" className="mb-6">
              <TriangleAlert />
              <AlertDescription>
                You are signed in as {user?.role === "admin" ? "an admin" : `a ${user?.role}`}{" "}
                account. Student onboarding only works for student accounts, so sign out and use a
                student account to finish it.
              </AlertDescription>
            </Alert>
          )}
          {activeStep === 0 && (
            <div className="flex flex-col gap-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Date of Birth" required error={errors.dob}>
                  <DatePicker
                    captionLayout="dropdown"
                    max={new Date().toISOString().slice(0, 10)}
                    value={formData.dob}
                    onChange={(v) => setFormData((p) => ({ ...p, dob: v }))}
                    required
                  />
                </Field>
                <Field label="Official Student ID" required error={errors.studentId}>
                  <Input
                    value={formData.studentId}
                    onChange={(e) => setFormData((p) => ({ ...p, studentId: e.target.value }))}
                    placeholder="e.g. 2024-04-XXXX"
                    required
                  />
                </Field>
              </div>

              <Field label="University / Institutional Body" required error={errors.university}>
                {({ id, describedBy, invalid }) => (
                  <Combobox
                    id={id}
                    aria-describedby={describedBy}
                    aria-invalid={invalid || undefined}
                    options={universityOptions}
                    value={selectedUniKey}
                    onChange={(key) => {
                      const value = key
                        ? (universities || []).find((u: UniversityOption) => uniKey(u) === key) ||
                          null
                        : null;
                      if (key && !value) return; // the kept current selection was re-picked
                      const uniId = value ? value.id : null;
                      setFormData((p) => ({
                        ...p,
                        university: value?.name || "",
                        universityId: uniId,
                        course: "", // Reset course when university changes
                      }));
                      setProgSearch("");
                    }}
                    onSearchChange={(value) => setUniSearch(value)}
                    loading={uniLoading}
                    placeholder="Search for your university..."
                    searchPlaceholder="Type to search universities…"
                    emptyText="No universities found"
                  />
                )}
              </Field>

              <Field label="Academic Programme / Course" required error={errors.course}>
                {({ id, describedBy, invalid }) => (
                  <Combobox
                    id={id}
                    aria-describedby={describedBy}
                    aria-invalid={invalid || undefined}
                    options={programmeOptions}
                    value={formData.course || null}
                    onChange={(name) => setFormData((p) => ({ ...p, course: name || "" }))}
                    onSearchChange={(value) => setProgSearch(value)}
                    loading={progLoading}
                    disabled={!formData.universityId}
                    placeholder={
                      formData.universityId
                        ? "Search for your programme..."
                        : "Select a university first"
                    }
                    searchPlaceholder="Type to search programmes…"
                    emptyText={
                      formData.universityId ? "No programmes found" : "Select a university first"
                    }
                  />
                )}
              </Field>

              <fieldset aria-describedby={errors.year ? "year-error" : undefined}>
                <legend className="mb-1.5 text-sm font-medium text-foreground">
                  Current Level of Study
                  <span className="ml-0.5 text-muted-foreground" aria-hidden>
                    *
                  </span>
                </legend>
                <div className="grid grid-cols-5 gap-1.5">
                  {[1, 2, 3, 4, 5].map((y) => {
                    const active = formData.year === String(y);
                    return (
                      <Button
                        key={y}
                        type="button"
                        variant={active ? "default" : "outline"}
                        aria-pressed={active}
                        onClick={() => setFormData((p) => ({ ...p, year: String(y) }))}
                        className={cn(
                          "px-0",
                          !active && errors.year && "border-destructive/70",
                        )}
                      >
                        <span>
                          <span className="max-[400px]:hidden">Year </span>
                          {y}
                        </span>
                      </Button>
                    );
                  })}
                </div>
                {errors.year && (
                  <p id="year-error" className="mt-1.5 text-caption text-destructive" role="alert">
                    {errors.year}
                  </p>
                )}
              </fieldset>
            </div>
          )}

          {activeStep === 1 && (
            <div className="flex flex-col gap-3">
              <p className="mb-1 text-sm text-body">
                Please upload your official university credentials for platform verification.
              </p>
              {[
                {
                  key: "deanLetter",
                  label: "University Dean Authorization",
                  format: "PDF",
                  icon: <ClipboardList />,
                },
                {
                  key: "universityId",
                  label: "Valid Institutional ID",
                  format: "Image or PDF",
                  icon: <BadgeCheck />,
                },
                {
                  key: "cv",
                  label: "Professional Dossier / CV",
                  format: "PDF",
                  icon: <FileText />,
                },
              ].map((doc) => (
                <DocumentUploadRow
                  key={doc.key}
                  label={doc.label}
                  hint={`Standard format: ${doc.format}`}
                  icon={doc.icon}
                  file={files[doc.key as keyof typeof files]}
                  onSelect={(f) => setFiles((p) => ({ ...p, [doc.key]: f }))}
                  onClear={() => setFiles((p) => ({ ...p, [doc.key]: null }))}
                />
              ))}
            </div>
          )}

          {activeStep === 2 && (
            <div className="flex flex-col gap-4">
              <Field label="Skills inventory">
                {({ id, describedBy, invalid }) => (
                  <MultiCombobox
                    id={id}
                    aria-describedby={describedBy}
                    aria-invalid={invalid || undefined}
                    options={skillOptions}
                    value={formData.selectedSkills}
                    onChange={(names) => setFormData((p) => ({ ...p, selectedSkills: names }))}
                    placeholder="Select expertise"
                    searchPlaceholder="Search skills…"
                    emptyText="No skills found."
                  />
                )}
              </Field>

              <Field label="Career Vision Statement">
                <Textarea
                  rows={3}
                  value={formData.careerGoals}
                  onChange={(e) => setFormData((p) => ({ ...p, careerGoals: e.target.value }))}
                  placeholder="Briefly describe your professional objectives for this field attachment..."
                />
              </Field>

              <Field label="Preferred Industry Sector">
                {({ id, describedBy, invalid }) => (
                  <Select
                    value={formData.preferredIndustry}
                    onValueChange={(v) => setFormData((p) => ({ ...p, preferredIndustry: v }))}
                  >
                    <SelectTrigger
                      id={id}
                      aria-describedby={describedBy}
                      aria-invalid={invalid || undefined}
                    >
                      <SelectValue placeholder="Select target industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industriesData?.map((ind: { id: string | number; name: string }) => (
                        <SelectItem
                          key={typeof ind === "string" ? ind : ind.id}
                          value={typeof ind === "string" ? ind : ind.name}
                        >
                          {typeof ind === "string" ? ind : ind.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>
          )}
        </div>

        <div className="mt-8 flex w-full gap-2">
          <Button
            variant="outline"
            size="lg"
            onClick={() =>
              activeStep === 0 ? navigate({ to: "/onboarding" }) : setActiveStep((p) => p - 1)
            }
            className="min-w-[120px]"
          >
            Go back
          </Button>
          <Button size="lg" onClick={handleNext} loading={loading} className="flex-1">
            {activeStep === steps.length - 1 ? "Finalize Onboarding" : "Continue"}
          </Button>
        </div>
      </div>
    </OnboardingLayout>
  );
}
